import { ClockCountdown, CaretDown, CaretUp } from '@phosphor-icons/react';
import { SelectBox, Option } from 'src/components/SelectBox';
import { useEffect, useRef, useState } from 'react';
import { cn } from 'src/utils/className';

type DaysAfterSelectorProps = {
  prevLabel: string;
  daysAfter?: number;
  onChange: (daysAfter: number) => void;
  disabled?: boolean;
};

export function DaysAfterSelector({
  prevLabel,
  daysAfter,
  onChange,
  disabled = false,
}: DaysAfterSelectorProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const unitOptions: Option[] = [
    { value: 'days', label: 'Days' },
    // Future support:
    // { value: 'hours', label: 'Hours' },
    // { value: 'minutes', label: 'Minutes' },
  ];

  // Currently we only support days in the data model
  const unitValue = unitOptions[0];

  useEffect(() => {
    const onClickAway = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const summaryLabel = `${Math.max(1, daysAfter || 1)} ${
    (daysAfter || 1) > 1 ? 'days' : 'day'
  }`;

  return (
    <div className="flex justify-center">
      <div className="relative inline-block" ref={containerRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'inline-flex items-center px-4 py-3 border border-neutral-200 rounded-2xl text-neutral-700 bg-neutral-0 hover:bg-neutral-50 transition',
            disabled && 'cursor-not-allowed opacity-50',
          )}
          disabled={disabled}
        >
          <ClockCountdown className="h-6 w-6 mr-2" />
          <p className="text-base truncate">
            <span className="font-medium">{summaryLabel}</span> after{' '}
            {prevLabel}
          </p>
          {open ? (
            <CaretUp className="ml-2 h-4 w-4" />
          ) : (
            <CaretDown className="ml-2 h-4 w-4" />
          )}
        </button>

        {open && (
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 bg-neutral-0 border border-neutral-200 shadow-lg rounded-xl p-4 flex items-center gap-3 w-max">
            <input
              type="number"
              min={1}
              max={365}
              step={1}
              value={Math.max(1, daysAfter || 1)}
              onChange={(e) => {
                const nextDaysAfter = Math.max(
                  1,
                  parseInt(e.target.value || '1', 10),
                );
                onChange(nextDaysAfter);
              }}
              className="w-24 appearance-none border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
            {/* TODO: Add back in when we have a way to select the unit of time for the delay
            <SelectBox
              options={unitOptions}
              value={unitValue}
              disabled
              size="small"
              variant="outlined"
              color="default"
              className="min-w-[8rem]"
            /> */}
            <span className="text-neutral-700 text-sm whitespace-nowrap">
              day{Number(Math.max(1, daysAfter || 1)) > 1 ? 's' : ''} after{' '}
              {prevLabel}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default DaysAfterSelector;
